/*
 * NEW ACCOUNT DIALOG
 *
 * Creates an account from the schema associated with the selected team.
 * Recent changes:
 * - Groups account fields for scanning, corrects presentation-only wording, and uses a creation-specific action label.
 * - Uses balanced header spacing and a tighter team-selector-to-form gap.
 * - Creates through TanStack Query and refreshes active account lists without reloading.
 */

import { FC, useState, useMemo } from 'react';
import { useTeams } from '../../context/TeamContext';
import { THEME_CLASSES } from '../../constants/themeConstants';
import SchemaBasedDetails from '../common/SchemaBasedDetails';
import { useSchemas } from '../../context/SchemaContext';
import { Button, DialogShell } from '../primitives';
import { useCreateAccountMutation } from '../../query/accountQueries';
import type { DynamicFormFieldGroup } from '../common/DynamicForm';

interface NewAccountProps {
  isOpen: boolean;
  onClose: () => void;
}

const ACCOUNT_CREATION_FIELD_GROUPS: ReadonlyArray<DynamicFormFieldGroup> = [
  {
    label: 'Account identity',
    fieldNames: ['name', 'legalName', 'brand', 'board', 'isActive', 'numberOfAgents'],
  },
  {
    label: 'Contact details',
    fieldNames: ['phoneNumber', 'website', 'socialSites'],
  },
  {
    label: 'Location and markets',
    fieldNames: ['streetAddress', 'city', 'province', 'markets', 'locations'],
  },
  {
    label: 'Sales pipeline',
    fieldNames: [
      'contractDate',
      'status',
      'targetCloseDate',
      'salesType',
      'repId',
      'potentialAcquirers',
      'origination',
    ],
  },
  {
    label: 'Outreach',
    fieldNames: ['nextOutreach', 'lastOutreach', 'disclosured'],
  },
];

const ACCOUNT_CREATION_FIELD_LABELS: Readonly<Record<string, string>> = {
  disclosured: 'Disclosure date',
};

const NewAccount: FC<NewAccountProps> = ({ isOpen, onClose }) => {
  const { teams, currentUserId } = useTeams();
  const createAccount = useCreateAccountMutation();
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const { schemas, isLoading: isLoadingSchemas } = useSchemas(2);
  const selectedTeamName = teams.find(team => team.teamId === selectedTeam)?.teamName;
  const isBusinessDevelopment = selectedTeamName === 'Business Development';
  const usesDirectFormLayout = isBusinessDevelopment || selectedTeamName === 'Commercial';

  // Get base and team schemas for creation
  const formSchemas = useMemo(() => {
    const validSchemas = schemas.filter(s => !s.rlp);

    // Auto-select team if only one valid schema exists
    if (validSchemas.length === 1 && !selectedTeam) {
      setSelectedTeam(validSchemas[0].teamId);
    }

    if (!selectedTeam) return [];

    // Get the team schema for the selected team
    const teamSchema = validSchemas.find(s => s.teamId === selectedTeam);
    return teamSchema ? [teamSchema] : [];
  }, [selectedTeam, schemas]);

  if (!isOpen) return null;

  const handleSubmit = async (formData: Record<string, any>) => {
    if (!currentUserId || !selectedTeam) return;

    try {
      const accountData = {
        ...formData,
        teamId: selectedTeam
      };

      await createAccount.mutateAsync({
        userId: currentUserId,
        name: formData.name || '',
        teamId: selectedTeam,
        data: JSON.stringify(accountData)
      });
      onClose();
    } catch (error) {
      console.error('Error creating account:', error);
      throw error;
    }
  };

  const handleSelectTeam = (teamId: number) => {
    setSelectedTeam(teamId === selectedTeam ? null : teamId);
  };

  return (
    <DialogShell
      open={isOpen}
      title="New Account"
      onClose={onClose}
      panelClassName="max-w-[800px] flex flex-col"
      headerClassName="!p-2"
      titleClassName="text-xl"
      closeButtonClassName="!h-7 !w-7"
      bodyClassName="!pt-0 min-h-0 flex-1"
    >

      <div className="mb-1 space-y-3">
        <div data-team-selector="true" className="flex flex-wrap justify-center gap-2">
          {teams
            .filter(team => schemas.some(s => s.teamId === team.teamId && !s.rlp))
            .map(team => (
              <Button
                key={team.teamId}
                onClick={() => handleSelectTeam(team.teamId)}
                size="sm"
                variant={selectedTeam === team.teamId ? 'primary' : 'secondary'}
                className="rounded-full"
              >
                {team.teamName}
              </Button>
            ))}
        </div>
        {selectedTeam && formSchemas.length === 0 && (
          <div className="text-center text-xs text-red-500">
            No account schema found for the selected team
          </div>
        )}
      </div>

      <div className="min-h-[100px]">
        {selectedTeam && formSchemas.length > 0 ? (
          <SchemaBasedDetails
            schemas={formSchemas}
            data={{}}
            onSubmit={handleSubmit}
            onCancel={onClose}
            isDeleteEnabled={false}
            isDisabled={() => false}
            showSectionHeaders={!usesDirectFormLayout}
            showCardSurface={!usesDirectFormLayout}
            constrainHeight={!isBusinessDevelopment}
            constrainFieldGrid={!isBusinessDevelopment}
            fieldTextSize="sm"
            compactRowSpacing
            submitLabel="Create account"
            fieldGroups={ACCOUNT_CREATION_FIELD_GROUPS}
            fieldLabelOverrides={ACCOUNT_CREATION_FIELD_LABELS}
            ungroupedFieldGroupLabel="Additional details"
          />
        ) : (
          <div className={`flex items-center justify-center h-full ${THEME_CLASSES.text.secondary}`}>
            {isLoadingSchemas ? 'Loading schema...' : 'Please select a team first'}
          </div>
        )}
      </div>
    </DialogShell>
  );
};

export default NewAccount;
